import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from 'hooks';
import { Cancel as IconCancel, Globe } from 'assets';
import { AccessibleIcon, Button } from 'components/partials';
import { selectLanguages, selectSelectedLanguage } from 'redux/root.selectors';
import { ILanguage, TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { useTranslate } from 'services';

import {
  FlagDe, FlagEe, FlagFr, FlagHr, FlagHu, FlagLt, FlagLv, FlagPl, FlagRu, FlagUa, FlagUs
} from 'assets/components/flags';
import { getSelectedLanguage } from 'redux/root.actions';

const Flags : { [key: string]: any } = {
  lv: <FlagLv />,
  et: <FlagEe />,
  fr: <FlagFr />,
  de: <FlagDe />,
  hu: <FlagHu />,
  ru: <FlagRu />,
  lt: <FlagLt />,
  pl: <FlagPl />,
  ua: <FlagUa />,
  en: <FlagUs />,
  ee: <FlagEe />,
  hr: <FlagHr />
};

const SettingsGearWrapper = styled.div`
  button {
    padding: .5rem;
    max-height: 2.5rem;
    min-height: 2.5rem;
  }
`;

const SettingsGearLanguage = styled.span`
  text-transform: uppercase;
  font-size: 0.813rem;
  font-weight: 800;
  padding-right: .1rem;
`;

const Container = styled.div<{ background?: string }>`
  background-color: ${({ background }) => background};
  position: absolute;
  z-index: 20;
  clear: both;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  min-height: var(--view-height);
  height: 100%;
  width: 100%;
  padding: 2.25rem 1.75rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: space-between;
`;

const Body = styled.div`
  margin-top: 4.125rem;
`;

const IconCancelWrapper = styled.div<{ hoverBorderColor: string; hoverShadowColor: string }>`
  border-radius: 3.125rem;
  border: 0.0625rem solid transparent;
  transition: all 0.3s ease-out;

  &:hover {
    border-color: ${({ hoverBorderColor }) => hoverBorderColor};
    box-shadow: 0 0 0 0.25rem ${({ hoverShadowColor }) => hoverShadowColor};
  }
`;

const CenterContainer = styled.div<{ background?: string }>`
  overflow-y: scroll;
  background: ${({ background }) => background};
  touch-action: manipulation;
  -webkit-overflow-scrolling: touch;
  max-height: 40%;
`;

const Select = styled.div<{ color?: string, borderBottomColor?: string }>`
  font-size: 1rem;
  line-height: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${({ borderBottomColor }) => borderBottomColor};
  color: ${({ color }) => color};
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LanguageFlag = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 7rem;
`;

const { accessibility, burger_menu } = TRANSLATIONS_VALUES_KEYS;

export const LanguageSelection: React.FC = () => {
  const { styles } = useTheme();

  const [showLanguageSelection, setShowLanguageSelection] = useState<boolean>(false);

  const dispatch = useDispatch();
  const languages: ILanguage[] = useSelector(selectLanguages);
  const selectedLanguage: ILanguage = useSelector(selectSelectedLanguage);
  const { translate } = useTranslate();

  const handleLangCodeChange = (langCode: string) => {
    const selectedLanguage = languages.find(({ code }: ILanguage) => code === langCode);
    dispatch(getSelectedLanguage(selectedLanguage as ILanguage));
    setShowLanguageSelection(false);
  };

  return (
    <>
      {
        languages.length > 1 && (
          <AccessibleIcon label={translate(TRANSLATIONS_VALUES_KEYS.accessibility.open_settings_view_btn_text)}>
            <SettingsGearWrapper>
              <Button
                role="button"
                tabIndex={0}
                data-testid="openSettingsBtn"
                bg={styles.colorSecondaryButtonBackground}
                color={styles.colorSecondaryButtonText}
                hoverBorderColor={styles.colorSecondaryButtonBackgroundDark}
                hoverShadowColor={styles.colorSecondaryButtonBackgroundLight}
                onClick={() => setShowLanguageSelection(true)}
              >
                <SettingsGearLanguage>
                  { selectedLanguage.code }
                </SettingsGearLanguage>
                {
                  Flags[selectedLanguage.code]
                    ? Flags[selectedLanguage.code]
                    : <Globe pathFill={styles.colorHeading} />
                }
              </Button>
            </SettingsGearWrapper>
          </AccessibleIcon>
        )
      }
      {
        showLanguageSelection && (
          <Container className="page" background={styles.colorBackgroundBase}>
            <Header>
              <h2 data-testid="settingTitle">{ translate(burger_menu.language_field) }</h2>
              <AccessibleIcon label={translate(accessibility.close_settings_view_btn_text)}>
                <IconCancelWrapper
                  role="button"
                  tabIndex={0}
                  data-testid="closeSettingsBtn"
                  hoverBorderColor={styles.colorPrimaryButtonBackground}
                  hoverShadowColor={styles.colorPrimaryButtonBackgroundLight}
                  onClick={() => setShowLanguageSelection(false)}
                >
                  <IconCancel fill={styles.colorPrimaryButtonBackground} />
                </IconCancelWrapper>
              </AccessibleIcon>
            </Header>
            <Body>
              {
                languages.length > 0 && (
                  <CenterContainer background={styles.colorBackgroundBase} data-testid="langSelector">
                    {
                      languages.filter(({ code }: ILanguage) => code !== selectedLanguage.code).map(({ id, code, name }: ILanguage) => (
                        <Select
                          key={id}
                          data-testid={`${code}Lang`.trim()}
                          onClick={() => handleLangCodeChange(code)}
                          color={styles.colorParagraph}
                          borderBottomColor={styles.colorBackgroundSurface}
                        >
                          <LanguageFlag>
                            { Flags[code] ? Flags[code] : <Globe pathFill={styles.colorHeading} /> }
                            { name }
                          </LanguageFlag>
                        </Select>
                      ))
                    }
                  </CenterContainer>
                )
              }
            </Body>
          </Container>
        )
      }
    </>
  );
};

export default LanguageSelection;
